import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, PlayCircle, Plus, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import CodeMirror from '@uiw/react-codemirror';
import { sql } from '@codemirror/lang-sql';
import { oneDark } from '@codemirror/theme-one-dark';

// Add a SQL query that behaves like a table in the model. The query is stored
// and injected as a CTE at run time, so it joins / filters / powers measures in
// both Insights and AI Explore — no physical table required.
export default function QueryTableDialog({ open, onOpenChange, modelId, onCreated, editEntity = null, entities = [] }) {
  const [name, setName] = useState('');
  const [sqlText, setSql] = useState('');
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // { columns, rows }
  const [error, setError] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const isEdit = !!editEntity;

  // Schema-aware autocomplete: feed the model's tables + columns to CodeMirror
  // so table and column names suggest as you type (plus SQL keywords).
  const sqlExtension = useMemo(() => {
    const schema = {};
    (entities || []).forEach(e => {
      const tbl = e.table_name || e.name;
      if (tbl) schema[tbl] = (e.attributes || []).map(a => a.name).filter(Boolean);
    });
    return sql({ schema, upperCaseKeywords: true });
  }, [entities]);

  const reset = () => { setName(''); setSql(''); setPreview(null); setError(''); setAiPrompt(''); setAiOpen(false); };

  // Prefill when opened to edit an existing query-table.
  React.useEffect(() => {
    if (open && editEntity) {
      setName(editEntity.name || '');
      setSql(editEntity.sql_query || '');
      setPreview(null); setError('');
    } else if (open && !editEntity) {
      reset();
    }
  }, [open, editEntity]);

  const validate = async () => {
    setError(''); setPreview(null);
    if (!sqlText.trim()) { setError('Enter a SELECT query first'); return; }
    setValidating(true);
    try {
      const res = await api.validateModelQuery(modelId, sqlText);
      if (!res.valid) { setError(res.error || 'Query is not valid'); }
      else { setPreview({ columns: res.columns || [], rows: res.rows || [] }); }
    } catch (e) { setError(e.message || 'Validation failed'); }
    setValidating(false);
  };

  const writeWithAi = async () => {
    if (!aiPrompt.trim()) { toast.error('Describe the query you want in plain English'); return; }
    setAiLoading(true); setError('');
    try {
      const res = await api.generateQuerySql(modelId, aiPrompt.trim());
      if (res.sql) {
        setSql(res.sql);
        setPreview(null);
        toast.success(res.valid ? 'Query drafted — review, then validate & preview' : 'Draft ready (needs a tweak) — review & validate');
      } else { toast.error('Could not draft a query for that'); }
    } catch (e) { toast.error(e.message || 'AI query writing failed'); }
    setAiLoading(false);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Give the query table a name'); return; }
    if (!preview) { toast.error('Validate the query first'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.updateSemanticEntity(editEntity.entity_id, {
          name: name.trim(),
          sql_query: sqlText.replace(/;\s*$/, ''),
          table_type: 'sql_query',
          attributes: preview.columns.map(c => ({
            name: c.name, display_name: c.name, data_type: c.data_type,
            semantic_type: c.data_type === 'number' ? 'measure' : 'dimension',
            ...(c.data_type === 'number' ? { aggregation: 'sum' } : {}),
          })),
        });
        toast.success(`Query table "${name.trim()}" updated`);
      } else {
        const ent = await api.createQueryEntity(modelId, { name: name.trim(), sql: sqlText, columns: preview.columns });
        toast.success(`Query table "${ent.name}" added with ${(ent.attributes || []).length} fields`);
      }
      reset();
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) { toast.error(e.message || 'Could not save query table'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="query-table-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit query table' : 'Add query as table'}</DialogTitle>
          <DialogDescription>Write a read-only SELECT. It becomes a virtual table you can join and build measures on.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Table name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Yearly Paid Losses" className="mt-1 text-sm" data-testid="query-table-name" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-muted-foreground">SQL query (SELECT only)</Label>
              <Button type="button" variant="ghost" size="sm" onClick={() => setAiOpen(v => !v)} className="h-6 text-[11px] px-2 text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/10" data-testid="query-table-ai-toggle">
                <Sparkles className="w-3 h-3 mr-1" /> Write with AI
              </Button>
            </div>
            {aiOpen && (
              <div className="mb-2 flex items-center gap-2 rounded-lg border border-indigo-500/25 bg-indigo-500/[0.06] p-2" data-testid="query-table-ai-panel">
                <Input
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !aiLoading) { e.preventDefault(); writeWithAi(); } }}
                  placeholder="Describe the query, e.g. 'yearly paid losses by line of business'"
                  className="text-xs h-8 bg-background/60"
                  data-testid="query-table-ai-prompt"
                />
                <Button size="sm" onClick={writeWithAi} disabled={aiLoading || !aiPrompt.trim()} className="h-8 text-xs whitespace-nowrap" data-testid="query-table-ai-generate">
                  {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Generate
                </Button>
              </div>
            )}
            <div className="mt-1 rounded-lg border border-border/40 overflow-hidden focus-within:ring-1 focus-within:ring-primary/40" data-testid="query-table-sql-wrap">
              <CodeMirror
                value={sqlText}
                height="180px"
                theme={oneDark}
                extensions={[sqlExtension]}
                onChange={setSql}
                basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: true, autocompletion: true }}
                placeholder={"SELECT ...\nFROM ...\nGROUP BY ..."}
                data-testid="query-table-sql"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={validate} disabled={validating || saving} className="text-xs" data-testid="query-table-validate">
              {validating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />} {isEdit ? 'Test / re-validate' : 'Validate & preview'}
            </Button>
            {preview && <span className="text-[11px] text-emerald-400">✓ {preview.columns.length} columns · {preview.rows.length} sample rows</span>}
          </div>

          {error && (
            <div className="flex items-start gap-2 text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2.5" data-testid="query-table-error">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /><span className="font-mono break-all">{error}</span>
            </div>
          )}

          {preview && preview.columns.length > 0 && (
            <div className="rounded-lg border border-border/30 overflow-auto max-h-64" data-testid="query-table-preview">
              <table className="w-full text-[11px]">
                <thead className="bg-muted/30 sticky top-0">
                  <tr>
                    {preview.columns.map(c => (
                      <th key={c.name} className="text-left px-2.5 py-1.5 font-medium text-foreground/80 whitespace-nowrap">
                        {c.name}
                        <span className={`ml-1 text-[8px] px-1 py-0.5 rounded ${c.data_type === 'number' ? 'bg-blue-500/15 text-blue-300' : 'bg-muted/40 text-muted-foreground'}`}>{c.data_type === 'number' ? 'measure' : 'dim'}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-t border-border/20">
                      {preview.columns.map(c => <td key={c.name} className="px-2.5 py-1 text-muted-foreground whitespace-nowrap font-mono">{String(r[c.name] ?? '')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => { reset(); onOpenChange(false); }} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving || !preview || !name.trim()} className="text-xs" data-testid="query-table-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} {isEdit ? 'Save changes' : 'Add to model'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
